<template>
    <b-modal
        id="assessment-join-modal"
        size="lg"
        title="평가 참여자 등록"
        body-class="bg-white"
        centered
        :no-close-on-backdrop="isLoading"
        :no-close-on-esc="isLoading"
        @hidden="onHidden"
    >
        <template #default>
            <p class="mb-3">
                <template v-if="targetUsers.length === 1">
                    <strong>{{ targetUsers[0].user_name }}</strong>님을 평가에 참여시킬 수 있습니다.
                </template>
                <template v-else>
                    <strong>{{ targetUsers.length }}명</strong>의 사용자를 평가에 참여시킬 수 있습니다.
                </template>
            </p>
            <b-table
                ref="projectTable"
                :items="projects"
                :fields="tableFields"
                selectable
                select-mode="multi"
                class="mb-0 custom-table"
                sticky-header="400px"
                show-empty
                empty-text="참여 중인 평가가 없습니다."
                @row-selected="onRowSelected"
            >
                <template #cell(selected)="{ rowSelected }">
                    <b-form-checkbox :checked="rowSelected" disabled />
                </template>
                <template #cell(status_code)="data">
                    <b-badge :variant="getProgressColor(data.item)">
                        {{ getProgressStatus(data.item) }}
                    </b-badge>
                </template>
            </b-table>
        </template>

        <template #modal-footer>
            <b-button variant="outline-secondary" @click="$bvModal.hide('assessment-join-modal')">
                건너뛰기
            </b-button>
            <b-button
                variant="primary"
                :disabled="selectedProjects.length === 0 || isLoading"
                @click="joinAssessments"
            >
                <b-spinner v-if="isLoading" small class="mr-1" />
                참여 등록
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import assessmentCommonMixin from '@shared/mixin/assessment.common.mixin';

export default {
    name: 'AssessmentJoinModal',
    mixins: [assessmentCommonMixin],
    props: {
        companyId: {
            type: Number,
            required: true,
        },
        projects: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            targetUsers: [],
            selectedProjects: [],
            isLoading: false,
        };
    },
    computed: {
        tableFields() {
            return [
                {
                    key: 'selected',
                    label: '',
                    thStyle: { width: '40px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'project_code',
                    label: '프로젝트',
                },
                {
                    key: 'period',
                    label: '참여기간',
                    thStyle: { width: '100px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'industry_name',
                    label: '평가산업',
                    thStyle: { width: '120px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'status_code',
                    label: '상태',
                    thStyle: { width: '80px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
            ];
        },
    },
    methods: {
        show(users) {
            if (!this.projects.length) {
                return;
            }
            this.targetUsers = users;
            this.selectedProjects = [];
            if (this.$refs.projectTable) {
                this.$refs.projectTable.clearSelected();
            }
            this.$bvModal.show('assessment-join-modal');
        },
        onRowSelected(rows) {
            this.selectedProjects = rows;
        },
        onHidden() {
            this.targetUsers = [];
            this.selectedProjects = [];
            this.isLoading = false;
        },
        async joinAssessments() {
            this.isLoading = true;
            try {
                const promises = [];
                this.selectedProjects.forEach((project) => {
                    this.targetUsers.forEach((user) => {
                        promises.push(
                            this.$axios.post('/admin/assessment/member/join', {
                                campaign_id: project.campaign_id,
                                company_id: this.companyId,
                                user_id: user.user_id,
                            })
                        );
                    });
                });
                const results = await Promise.allSettled(promises);
                const failedCount = results.filter(r => r.status === 'rejected').length;
                const succeededCount = results.filter(r => r.status === 'fulfilled').length;

                if (failedCount === 0) {
                    this.$EventBus.$emit('toast-success', '평가 참여 등록', '평가 참여 등록이 완료되었습니다.');
                    this.$bvModal.hide('assessment-join-modal');
                    this.$emit('completed');
                } else {
                    const message = succeededCount > 0
                        ? `일부 등록이 실패했습니다. (성공: ${succeededCount}건, 실패: ${failedCount}건) 다시 시도해도 성공한 항목은 중복 적용되지 않습니다.`
                        : getErrorMessage(results[0].reason, '평가 참여 등록 중 오류가 발생했습니다.');
                    this.$EventBus.$emit('toast-failure', '평가 참여 등록', message);
                }
            } catch (e) {
                console.error(e);
                this.$EventBus.$emit('toast-failure', '평가 참여 등록', '평가 참여 등록 중 오류가 발생했습니다.');
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
